// Global Realtime Manager - Radical Fix for Connection Issues
import { supabase } from "@/integrations/supabase/client";
import { RealtimeChannel } from "@supabase/supabase-js";

interface ChannelSubscription {
  channel: RealtimeChannel;
  userId: string;
  type: 'messages' | 'conversations' | 'typing';
  callbacks: Set<(payload?: any) => void>;
  retryCount: number;
  lastConnected: number;
}

class GlobalRealtimeManager {
  private channels = new Map<string, ChannelSubscription>();
  private connectionHealth = new Map<string, boolean>();
  private cleanupTimer: NodeJS.Timeout | null = null;
  private healthCheckTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Start health monitoring
    this.startHealthMonitoring();
    // Cleanup stale connections every 30 seconds
    this.cleanupTimer = setInterval(() => this.cleanupStaleConnections(), 30000);
    
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  // Nuclear reset - Force cleanup ALL channels for a user
  nuclearReset(userId: string) {
    console.log(`🚨 [GlobalRT] Nuclear reset for user: ${userId}`);
    
    // Find all channels for this user
    const userChannels = Array.from(this.channels.entries())
      .filter(([_, sub]) => sub.userId === userId);

    // Force remove each channel
    for (const [channelId, subscription] of userChannels) {
      try {
        supabase.removeChannel(subscription.channel);
        this.channels.delete(channelId);
        this.connectionHealth.delete(channelId);
        console.log(`🧹 [GlobalRT] Removed channel: ${channelId}`);
      } catch (error) {
        console.warn(`⚠️ [GlobalRT] Error removing channel ${channelId}:`, error);
      }
    }
  }

  // Subscribe to messages with automatic deduplication
  subscribeToMessages(userId: string, callback: (payload?: any) => void) {
    const channelId = `messages-${userId}`;
    
    // Check if we already have this subscription
    if (this.channels.has(channelId)) {
      const existing = this.channels.get(channelId)!;
      existing.callbacks.add(callback);
      console.log(`📎 [GlobalRT] Added callback to existing channel: ${channelId}`);
      return () => this.unsubscribeCallback(channelId, callback);
    }

    // Create new subscription
    console.log(`🔌 [GlobalRT] Creating new messages subscription: ${channelId}`);
    
    const channel = supabase
      .channel(channelId, {
        config: {
          presence: { key: userId },
        },
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${userId}`
        },
        (payload) => {
          console.log(`📨 [GlobalRT] Message event received for ${userId}`);
          this.notifyCallbacks(channelId, payload);
        }
      )
      .subscribe((status, err) => {
        this.handleSubscriptionStatus(channelId, status, err);
      });

    // Store subscription
    this.channels.set(channelId, {
      channel,
      userId,
      type: 'messages',
      callbacks: new Set([callback]),
      retryCount: 0,
      lastConnected: Date.now()
    });

    return () => this.unsubscribeCallback(channelId, callback);
  }

  // Subscribe to conversations
  subscribeToConversations(userId: string, callback: (payload?: any) => void) {
    const channelId = `conversations-${userId}`;
    
    if (this.channels.has(channelId)) {
      const existing = this.channels.get(channelId)!;
      existing.callbacks.add(callback);
      return () => this.unsubscribeCallback(channelId, callback);
    }

    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          this.notifyCallbacks(channelId, payload);
        }
      )
      .subscribe((status, err) => {
        this.handleSubscriptionStatus(channelId, status, err);
      });

    this.channels.set(channelId, {
      channel,
      userId,
      type: 'conversations',
      callbacks: new Set([callback]),
      retryCount: 0,
      lastConnected: Date.now()
    });

    return () => this.unsubscribeCallback(channelId, callback);
  }

  private handleSubscriptionStatus(channelId: string, status: string, err?: any) {
    const subscription = this.channels.get(channelId);
    if (!subscription) return;

    console.log(`🔌 [GlobalRT] Channel ${channelId} status: ${status}`);

    if (err) {
      console.error(`❌ [GlobalRT] Subscription error for ${channelId}:`, err);
      this.connectionHealth.set(channelId, false);
      return;
    }

    switch (status) {
      case 'SUBSCRIBED':
        console.log(`✅ [GlobalRT] Successfully connected: ${channelId}`);
        this.connectionHealth.set(channelId, true);
        subscription.retryCount = 0;
        subscription.lastConnected = Date.now();
        break;
        
      case 'CHANNEL_ERROR':
      case 'TIMED_OUT':
        console.log(`⚠️ [GlobalRT] Connection issue for ${channelId}: ${status}`);
        this.connectionHealth.set(channelId, false);
        this.scheduleReconnect(channelId);
        break;
        
      case 'CLOSED':
        console.log(`🔌 [GlobalRT] Connection closed: ${channelId}`);
        this.connectionHealth.set(channelId, false);
        break;
    }
  }

  private scheduleReconnect(channelId: string) {
    const subscription = this.channels.get(channelId);
    if (!subscription || subscription.retryCount >= 5) { // Reduced max retries
      console.log(`❌ [GlobalRT] Max retries reached for ${channelId}, giving up`);
      this.connectionHealth.set(channelId, false);
      return;
    }

    subscription.retryCount++;
    
    // Progressive backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, subscription.retryCount - 1), 16000);

    console.log(`🔄 [GlobalRT] Scheduling reconnect for ${channelId} in ${delay}ms (attempt ${subscription.retryCount})`);

    setTimeout(() => {
      if (this.channels.has(channelId)) {
        // Check if we're still offline before attempting reconnect
        if (navigator.onLine === false) {
          console.log(`📡 [GlobalRT] Still offline, delaying reconnect for ${channelId}`);
          this.scheduleReconnect(channelId);
          return;
        }
        this.reconnectChannel(channelId);
      }
    }, delay);
  }

  private reconnectChannel(channelId: string) {
    const subscription = this.channels.get(channelId);
    if (!subscription) return;

    console.log(`🔄 [GlobalRT] Reconnecting channel: ${channelId}`);

    // Remove old channel
    try {
      supabase.removeChannel(subscription.channel);
    } catch (error) {
      console.warn(`⚠️ [GlobalRT] Error removing old channel:`, error);
    }

    // Create new subscription immediately with same settings
    const newChannel = supabase
      .channel(channelId, {
        config: {
          presence: { key: subscription.userId },
        },
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${subscription.userId}`
        },
        (payload) => {
          console.log(`📨 [GlobalRT] Message event received for ${subscription.userId}`);
          this.notifyCallbacks(channelId, payload);
        }
      )
      .subscribe((status, err) => {
        this.handleSubscriptionStatus(channelId, status, err);
      });

    // Update the subscription with new channel
    subscription.channel = newChannel;
    subscription.lastConnected = Date.now();
  }

  private notifyCallbacks(channelId: string, payload?: any) {
    const subscription = this.channels.get(channelId);
    if (!subscription) return;

    subscription.callbacks.forEach(callback => {
      try {
        callback(payload);
      } catch (error) {
        console.error(`❌ [GlobalRT] Error in callback for ${channelId}:`, error);
      }
    });
  }

  private unsubscribeCallback(channelId: string, callback: (payload?: any) => void) {
    const subscription = this.channels.get(channelId);
    if (!subscription) return;

    subscription.callbacks.delete(callback);

    // If no more callbacks, remove the entire subscription
    if (subscription.callbacks.size === 0) {
      console.log(`🧹 [GlobalRT] Removing unused channel: ${channelId}`);
      try {
        supabase.removeChannel(subscription.channel);
        this.channels.delete(channelId);
        this.connectionHealth.delete(channelId);
      } catch (error) {
        console.warn(`⚠️ [GlobalRT] Error removing channel:`, error);
      }
    }
  }

  private startHealthMonitoring() {
    this.healthCheckTimer = setInterval(() => {
      const now = Date.now();
      const staleThreshold = 60000; // 1 minute

      for (const [channelId, subscription] of this.channels.entries()) {
        const timeSinceLastConnection = now - subscription.lastConnected;
        const isHealthy = this.connectionHealth.get(channelId) || false;

        if (!isHealthy && timeSinceLastConnection > staleThreshold) {
          console.log(`🩺 [GlobalRT] Health check: reconnecting stale channel ${channelId}`);
          this.reconnectChannel(channelId);
        }
      }
    }, 30000); // Check every 30 seconds
  }

  private cleanupStaleConnections() {
    const now = Date.now();
    const staleThreshold = 300000; // 5 minutes

    for (const [channelId, subscription] of this.channels.entries()) {
      if (subscription.callbacks.size === 0 && now - subscription.lastConnected > staleThreshold) {
        console.log(`🧹 [GlobalRT] Cleaning up stale connection: ${channelId}`);
        try {
          supabase.removeChannel(subscription.channel);
          this.channels.delete(channelId);
          this.connectionHealth.delete(channelId);
        } catch (error) {
          console.warn(`⚠️ [GlobalRT] Error cleaning up stale connection:`, error);
        }
      }
    }
  }

  // Get connection health for monitoring
  getConnectionHealth() {
    const health: Record<string, any> = {};
    
    for (const [channelId, subscription] of this.channels.entries()) {
      health[channelId] = {
        isHealthy: this.connectionHealth.get(channelId) || false,
        retryCount: subscription.retryCount,
        lastConnected: new Date(subscription.lastConnected).toISOString(),
        callbackCount: subscription.callbacks.size,
        type: subscription.type
      };
    }
    
    return health;
  }

  // Force sync - for debugging
  forceSync(userId: string) {
    console.log(`🔄 [GlobalRT] Force sync for user: ${userId}`);
    this.nuclearReset(userId);
    
    // Wait a bit then reestablish connections
    setTimeout(() => {
      console.log(`🔌 [GlobalRT] Reestablishing connections after force sync`);
      // The components will automatically resubscribe when they detect the connection is gone
    }, 1000);
  }

  // Handle online/offline events
  private handleOnline() {
    console.log(`📡 [GlobalRT] Network came back online, reconnecting channels`);
    for (const [channelId, subscription] of this.channels.entries()) {
      if (!this.connectionHealth.get(channelId)) {
        console.log(`🔄 [GlobalRT] Auto-reconnecting ${channelId} after coming online`);
        subscription.retryCount = 0; // Reset retry count when network comes back
        this.reconnectChannel(channelId);
      }
    }
  }

  private handleOffline() {
    console.log(`📡 [GlobalRT] Network went offline`);
    // Mark all connections as unhealthy
    for (const channelId of this.channels.keys()) {
      this.connectionHealth.set(channelId, false);
    }
  }

  // Cleanup everything
  destroy() {
    console.log(`🧹 [GlobalRT] Destroying Global Realtime Manager`);
    
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
    }

    // Remove event listeners
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));

    // Remove all channels
    for (const [channelId, subscription] of this.channels.entries()) {
      try {
        supabase.removeChannel(subscription.channel);
      } catch (error) {
        console.warn(`⚠️ [GlobalRT] Error removing channel during destroy:`, error);
      }
    }

    this.channels.clear();
    this.connectionHealth.clear();
  }
}

// Global singleton instance
export const globalRealtimeManager = new GlobalRealtimeManager();

// Export types
export type { ChannelSubscription };